import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Eye } from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { Patient } from '../store/slices/patientsSlice';
import {
  Card,
  Input,
  Button,
  CardContent,
  Checkbox,
} from '@roger/ui-components';
import PatientSnapshotDialog from '../components/PatientSnapshotDialog';
import { TestPatientBadge } from '../components/patient/TestPatientBadge';
import { formatDateToMMDDYYYY } from '../utils/dateUtils';
import {
  ALL_CLINICS,
  useAccessibleClinics,
} from '../hooks/useAccessibleClinics';
import { ClinicSelect } from '../components/clinic/ClinicSelect';

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const formatCityState = (patient: Patient) => {
  const parts: string[] = [];
  if (patient.city) parts.push(patient.city);
  if (patient.state) parts.push(patient.state);
  return parts.length > 0 ? parts.join(', ') : 'N/A';
};

const Patients: React.FC = () => {
  const navigate = useNavigate();
  const {
    patients,
    isPatientsLoading: loading,
    error,
    fetchPatients,
    searchTerm,
    setSearchTerm,
    showInactive,
    setShowInactive,
    hideTest,
    setHideTest,
  } = useStore(
    useShallow(state => ({
      patients: state.patients,
      isPatientsLoading: state.isPatientsLoading,
      error: state.error,
      fetchPatients: state.fetchPatients,
      searchTerm: state.searchTerm,
      setSearchTerm: state.setSearchTerm,
      showInactive: state.showInactive,
      setShowInactive: state.setShowInactive,
      hideTest: state.hideTest,
      setHideTest: state.setHideTest,
    }))
  );

  const {
    clinics: clinicOptions,
    accessibleClinicIds,
    selectedClinicId,
    setSelectedClinicId,
    isAllClinics,
  } = useAccessibleClinics();

  const [alpha, setAlpha] = useState('');
  const [snapshotPatient, setSnapshotPatient] = useState<Patient | null>(null);
  const [snapshotOpen, setSnapshotOpen] = useState(false);

  // Ensure patients is always an array
  const safePatients = patients || [];

  // Fetch patients on component mount and when the server-side filters change
  useEffect(() => {
    fetchPatients();
  }, [showInactive, hideTest]);

  // Filtered patients by search, alpha, and clinic
  const filteredPatients = safePatients.filter((p: Patient) => {
    try {
      const matchesSearch =
        (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.mrn && p.mrn.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesAlpha =
        !alpha ||
        (p.first_name || '')[0]?.toUpperCase() === alpha ||
        (p.last_name || '')[0]?.toUpperCase() === alpha;
      const matchesClinic = isAllClinics
        ? p.clinic_id != null && accessibleClinicIds.includes(p.clinic_id)
        : p.clinic_id === selectedClinicId;
      return matchesSearch && matchesAlpha && matchesClinic;
    } catch (error) {
      console.error('Error filtering patient:', error, p);
      return false;
    }
  });

  // For alpha navigation, count patients per letter (first or last name)
  const alphaCounts: Record<string, number> = {};
  safePatients.forEach((p: Patient) => {
    try {
      const firstLetter = (p.first_name || '')[0]?.toUpperCase();
      const lastLetter = (p.last_name || '')[0]?.toUpperCase();
      if (firstLetter) {
        alphaCounts[firstLetter] = (alphaCounts[firstLetter] || 0) + 1;
      }
      if (lastLetter && lastLetter !== firstLetter) {
        alphaCounts[lastLetter] = (alphaCounts[lastLetter] || 0) + 1;
      }
    } catch (error) {
      console.error('Error processing patient for alpha navigation:', error, p);
      alphaCounts['?'] = (alphaCounts['?'] || 0) + 1;
    }
  });

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h1 className='text-2xl font-bold'>Patient Manager</h1>
        <p className='text-muted-foreground text-sm'>
          Comprehensive patient management with AI-powered insights and
          analytics
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <Card className='border-destructive bg-destructive/10'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 text-destructive'>
              <AlertTriangle className='h-4 w-4' />
              <span className='font-medium'>Error loading patients</span>
            </div>
            <p className='text-sm text-destructive/80 mt-1'>{error}</p>
            <Button
              variant='outline'
              size='sm'
              className='mt-2'
              onClick={() => fetchPatients()}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Patient List Controls */}
      <Card>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4'>
          <Input
            placeholder='Search all agency patients - Name, MRN...'
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(e.target.value)
            }
            className='flex-1'
          />
          {clinicOptions.length > 0 && (
            <ClinicSelect
              className='w-full sm:w-56 shrink-0'
              clinics={clinicOptions}
              includeAllOption
              allOptionValue={ALL_CLINICS}
              value={selectedClinicId}
              onChange={e => setSelectedClinicId(e.target.value)}
              placeholder='Filter by clinic'
            />
          )}
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4'>
            <div className='flex items-center gap-2 shrink-0'>
              <Checkbox
                id='showInactive'
                checked={showInactive}
                onCheckedChange={(checked: boolean) =>
                  setShowInactive(!!checked)
                }
              />
              <label
                htmlFor='showInactive'
                className='text-sm text-muted-foreground cursor-pointer select-none whitespace-nowrap'
              >
                Show inactive patients
              </label>
            </div>
            <div className='flex items-center gap-2 shrink-0'>
              <Checkbox
                id='hideTest'
                checked={hideTest}
                onCheckedChange={(checked: boolean) => setHideTest(!!checked)}
              />
              <label
                htmlFor='hideTest'
                className='text-sm text-muted-foreground cursor-pointer select-none whitespace-nowrap'
              >
                Hide test patients
              </label>
            </div>
          </div>
        </div>
        {/* Alphabetical Navigation */}
        <div className='mt-4 flex flex-wrap gap-1'>
          {alphabet.map(letter => (
            <Button
              key={letter}
              variant={alpha === letter ? 'default' : 'outline'}
              size='sm'
              className='px-2 py-1 text-xs'
              onClick={() => setAlpha(alpha === letter ? '' : letter)}
              disabled={!alphaCounts[letter]}
            >
              {letter}
            </Button>
          ))}
        </div>
      </Card>

      {/* Patient Cards List */}
      <Card>
        {!loading && (
          <div className='mb-2 text-xs text-muted-foreground'>
            {alpha} ({filteredPatients.length} of {safePatients.length}{' '}
            patients)
          </div>
        )}
        <div className='flex flex-wrap gap-3'>
          {loading ? (
            <div className='text-xs text-muted-foreground'>
              Loading patients...
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className='text-xs text-muted-foreground'>
              No patients found.
            </div>
          ) : (
            filteredPatients.map(patient => {
              try {
                return (
                  <Card
                    key={patient.id}
                    className='p-3 sm:p-3 md:p-3 w-full sm:w-64 min-w-0 cursor-pointer hover:shadow-sm transition-shadow relative'
                    onClick={e => {
                      // Only navigate if not clicking Eye icon
                      if ((e.target as HTMLElement).closest('.eye-btn')) return;
                      navigate(`/patients/${patient.id}`);
                    }}
                  >
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-1.5'>
                        <div className='font-medium truncate text-sm'>
                          {patient.name || 'Unknown Patient'}
                        </div>
                        {patient.is_test_patient && (
                          <TestPatientBadge className='shrink-0' />
                        )}
                      </div>
                      <div className='text-xs text-muted-foreground space-y-0.5 mt-1'>
                        <div className='truncate'>
                          MRN: {patient.mrn || 'N/A'}
                        </div>
                        <div className='truncate'>
                          DOB:{' '}
                          {formatDateToMMDDYYYY(patient.date_of_birth) || 'N/A'}
                        </div>
                        <div className='truncate'>
                          {patient.address || 'N/A'}
                          {(patient.city || patient.state) && ', '}
                          {formatCityState(patient)}
                        </div>
                        <div className='truncate'>
                          Insurance: {patient.insurance_name || 'N/A'}
                        </div>
                        <div className='truncate'>
                          Clinic: {patient.clinic_name || 'N/A'}
                        </div>
                      </div>
                    </div>
                    {/* Eye Icon Button */}
                    <button
                      className='eye-btn absolute top-2 right-2 p-1 rounded hover:bg-muted'
                      onClick={e => {
                        e.stopPropagation();
                        setSnapshotPatient(patient);
                        setSnapshotOpen(true);
                      }}
                      aria-label='View Snapshot'
                      type='button'
                    >
                      <Eye className='w-5 h-5 text-muted-foreground' />
                    </button>
                  </Card>
                );
              } catch (error) {
                console.error('Error rendering patient card:', error, patient);
                return (
                  <Card
                    key={patient.id || 'error'}
                    className='p-3 sm:p-3 md:p-3 w-full sm:w-64 min-w-0'
                  >
                    <div className='flex-1 min-w-0'>
                      <div className='font-medium truncate text-sm text-destructive'>
                        Error Loading Patient
                      </div>
                      <div className='text-xs text-muted-foreground truncate'>
                        ID: {patient.id || 'Unknown'}
                      </div>
                    </div>
                  </Card>
                );
              }
            })
          )}
        </div>
      </Card>
      {/* Patient Snapshot Dialog */}
      <PatientSnapshotDialog
        patient={snapshotPatient as Patient}
        open={snapshotOpen}
        onClose={() => setSnapshotOpen(false)}
      />
    </div>
  );
};

export default Patients;
